import React, { useState } from "react";
import axios from "axios";
import {
  ArrowRight,
  CheckCircle2,
  AlertTriangle,
  Building2,
  Wallet,
  Users,
  Landmark,
  ShieldCheck,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const INITIAL = {
  corporation_name: "", dba_name: "", physical_address: "", city: "", state: "", zip: "",
  phone_number: "", email_address: "", url: "", business_contact: "", products_services: "",
  state_incorporated: "", month_year_started: "", ownership_type: "", number_of_employees: "",
  federal_tax_id: "", average_monthly_volume: "", average_transaction: "", highest_transaction: "",
  equipment_gateway: "", pct_swiped: "", pct_keyed: "", pct_phone: "", pct_internet: "",
  owner1_full_name: "", owner1_title: "", owner1_ownership_pct: "", owner1_dob: "", owner1_ssn: "",
  owner1_address: "", owner1_city: "", owner1_state: "", owner1_zip: "", owner1_cell_phone: "", owner1_drivers_license: "",
  owner2_full_name: "", owner2_title: "", owner2_ownership_pct: "", owner2_dob: "", owner2_ssn: "",
  owner2_address: "", owner2_city: "", owner2_state: "", owner2_zip: "", owner2_cell_phone: "", owner2_drivers_license: "",
  bank_name: "", routing_number: "", account_number: "",
  signature_name: "", signature_title: "", signature_date: new Date().toISOString().slice(0, 10), acknowledged: false,
};

const SECTIONS = [
  { id: "business", n: "01", title: "Business Information", icon: Building2 },
  { id: "financial", n: "02", title: "Financial Profile", icon: Wallet },
  { id: "ownership", n: "03", title: "Ownership", icon: Users },
  { id: "deposit", n: "04", title: "Deposit Account", icon: Landmark },
  { id: "sign", n: "05", title: "Acknowledge & Sign", icon: ShieldCheck },
];

export default function MerchantApplicationPage() {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [showOwner2, setShowOwner2] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.acknowledged) {
      setStatus({ state: "error", msg: "Please acknowledge the application before submitting." });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${BACKEND_URL}/api/merchant-application`, form);
      setStatus({
        state: "success",
        msg: "Application received. A MerchantCore underwriting specialist will reach out within 1 business day to confirm next steps.",
      });
      setForm(INITIAL);
      setShowOwner2(false);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Something went wrong. Please try again.";
      setStatus({ state: "error", msg: typeof detail === "string" ? detail : "Please check your inputs and try again." });
    }
  };

  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="apply-hero">
        <div aria-hidden className="pointer-events-none absolute inset-0 grid-overlay opacity-60" />
        <div aria-hidden className="pointer-events-none absolute -top-40 right-10 h-[420px] w-[420px] rounded-full bg-emerald/10 blur-[120px]" />
        <div className="container-wide relative z-10 grid items-end gap-10 py-20 lg:grid-cols-12 lg:py-24">
          <div className="lg:col-span-9">
            <span className="eyebrow">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald" /> Merchant application
            </span>
            <h1 className="mt-7 font-display text-4xl font-bold leading-[1.02] tracking-tight text-cloud sm:text-6xl lg:text-7xl">
              High-risk merchant
              <br />
              account <span className="text-gradient">application.</span>
            </h1>
          </div>
          <div className="lg:col-span-3">
            <p className="font-sans text-sm leading-relaxed text-muted">
              Complete the application below to get started. All information is
              kept strictly confidential and used only for underwriting.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION INDEX STRIP */}
      <section className="border-y border-line bg-panel/40" data-testid="apply-section-index">
        <div className="container-wide">
          <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5">
            {SECTIONS.map(({ id, n, title, icon: Icon }) => (
              <li key={id} className="flex items-center gap-3 px-2 py-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-line bg-emerald/10 text-emerald">
                  <Icon className="h-4 w-4" />
                </span>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-ultra text-faint">{n}</div>
                  <a href={`#${id}`} className="font-sans text-xs font-medium text-cloud/90 hover:text-emerald">{title}</a>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <form onSubmit={onSubmit} data-testid="merchant-app-form">
        <FormSection id="business" n="01" title="Business Information" subtitle="Tell us about the entity that will process payments.">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Corporation name" required>
              <input name="corporation_name" required value={form.corporation_name} onChange={onChange} className="input" placeholder="Acme Holdings, Inc." data-testid="apply-corporation-name" />
            </Field>
            <Field label="DBA name">
              <input name="dba_name" value={form.dba_name} onChange={onChange} className="input" placeholder="Acme" data-testid="apply-dba-name" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Physical address" required>
                <input name="physical_address" required value={form.physical_address} onChange={onChange} className="input" placeholder="123 Market St, Suite 400" data-testid="apply-physical-address" />
              </Field>
            </div>
            <Field label="City" required>
              <input name="city" required value={form.city} onChange={onChange} className="input" data-testid="apply-city" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="State" required>
                <input name="state" required value={form.state} onChange={onChange} className="input" placeholder="NY" data-testid="apply-state" />
              </Field>
              <Field label="ZIP" required>
                <input name="zip" required value={form.zip} onChange={onChange} className="input" placeholder="10001" data-testid="apply-zip" />
              </Field>
            </div>
            <Field label="Phone number" required>
              <input name="phone_number" required value={form.phone_number} onChange={onChange} className="input" placeholder="+1 555 …" data-testid="apply-phone" />
            </Field>
            <Field label="Email address" required>
              <input type="email" name="email_address" required value={form.email_address} onChange={onChange} className="input" placeholder="founder@company.com" data-testid="apply-email" />
            </Field>
            <Field label="Website (URL)">
              <input name="url" value={form.url} onChange={onChange} className="input" placeholder="https://" data-testid="apply-url" />
            </Field>
            <Field label="Business contact" required>
              <input name="business_contact" required value={form.business_contact} onChange={onChange} className="input" placeholder="Primary contact name" data-testid="apply-business-contact" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Products / services sold" required>
                <textarea name="products_services" required rows={3} value={form.products_services} onChange={onChange} className="input resize-y" placeholder="Describe what you sell, who buys it, and how it ships." data-testid="apply-products-services" />
              </Field>
            </div>
          </div>
        </FormSection>

        <FormSection id="financial" n="02" title="Financial Profile" subtitle="Helps underwriting size the relationship.">
          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="State incorporated" required>
              <input name="state_incorporated" required value={form.state_incorporated} onChange={onChange} className="input" placeholder="DE" data-testid="apply-state-incorporated" />
            </Field>
            <Field label="Month / year started" required>
              <input name="month_year_started" required value={form.month_year_started} onChange={onChange} className="input" placeholder="MM/YYYY" data-testid="apply-month-year-started" />
            </Field>
            <Field label="Ownership type" required>
              <select name="ownership_type" required value={form.ownership_type} onChange={onChange} className="input" data-testid="apply-ownership-type">
                <option value="">Select…</option>
                <option>Sole Proprietor</option>
                <option>LLC</option>
                <option>S-Corp</option>
                <option>C-Corp</option>
                <option>Partnership</option>
                <option>Non-Profit</option>
                <option>Other</option>
              </select>
            </Field>
            <Field label="Number of employees">
              <input name="number_of_employees" value={form.number_of_employees} onChange={onChange} className="input" placeholder="e.g. 12" data-testid="apply-employees" />
            </Field>
            <Field label="Federal tax ID (EIN)" required>
              <input name="federal_tax_id" required value={form.federal_tax_id} onChange={onChange} className="input" placeholder="00-0000000" data-testid="apply-ein" />
            </Field>
            <Field label="Average monthly sales volume" required>
              <input name="average_monthly_volume" required value={form.average_monthly_volume} onChange={onChange} className="input" placeholder="$" data-testid="apply-avg-volume" />
            </Field>
            <Field label="Average transaction" required>
              <input name="average_transaction" required value={form.average_transaction} onChange={onChange} className="input" placeholder="$" data-testid="apply-avg-tx" />
            </Field>
            <Field label="Highest transaction" required>
              <input name="highest_transaction" required value={form.highest_transaction} onChange={onChange} className="input" placeholder="$" data-testid="apply-highest-tx" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Equipment / gateway">
                <input name="equipment_gateway" value={form.equipment_gateway} onChange={onChange} className="input" placeholder="e.g. NMI, Authorize.net, Stripe Terminal" data-testid="apply-equipment-gateway" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <div className="label">Percentage of sales (must total 100%)</div>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <Field label="Swiped %"><input name="pct_swiped" value={form.pct_swiped} onChange={onChange} className="input" placeholder="0" data-testid="apply-pct-swiped" /></Field>
                <Field label="Keyed %"><input name="pct_keyed" value={form.pct_keyed} onChange={onChange} className="input" placeholder="0" data-testid="apply-pct-keyed" /></Field>
                <Field label="Phone %"><input name="pct_phone" value={form.pct_phone} onChange={onChange} className="input" placeholder="0" data-testid="apply-pct-phone" /></Field>
                <Field label="Internet %"><input name="pct_internet" value={form.pct_internet} onChange={onChange} className="input" placeholder="0" data-testid="apply-pct-internet" /></Field>
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection id="ownership" n="03" title="Ownership" subtitle="Information for each owner with ≥ 25% stake.">
          <OwnerBlock idx="1" form={form} onChange={onChange} required />
          {!showOwner2 && (
            <button type="button" onClick={() => setShowOwner2(true)} className="btn-secondary mt-8" data-testid="apply-add-owner-2">
              + Add second owner
            </button>
          )}
          {showOwner2 && (
            <>
              <div className="my-10 rule" />
              <OwnerBlock idx="2" form={form} onChange={onChange} />
              <button type="button" onClick={() => setShowOwner2(false)} className="mt-6 font-mono text-[11px] uppercase tracking-wide2 text-muted hover:text-emerald" data-testid="apply-remove-owner-2">
                — Remove second owner
              </button>
            </>
          )}
        </FormSection>

        <FormSection id="deposit" n="04" title="Deposit Account" subtitle="Where settlements will be deposited.">
          <div className="grid gap-5 sm:grid-cols-3">
            <Field label="Bank name" required>
              <input name="bank_name" required value={form.bank_name} onChange={onChange} className="input" placeholder="e.g. Chase" data-testid="apply-bank-name" />
            </Field>
            <Field label="Routing number" required>
              <input name="routing_number" required value={form.routing_number} onChange={onChange} className="input" placeholder="9 digits" data-testid="apply-routing" />
            </Field>
            <Field label="Account number" required>
              <input name="account_number" required value={form.account_number} onChange={onChange} className="input" placeholder="…" data-testid="apply-account" />
            </Field>
          </div>
        </FormSection>

        <FormSection id="sign" n="05" title="Acknowledge & Sign" subtitle="Electronic signature has the same legal effect as a handwritten signature." last>
          <div className="rounded-2xl border border-line bg-white/[0.02] p-5">
            <p className="font-sans text-sm leading-relaxed text-cloud/85">
              By signing below, the undersigned authorized signer (i) certifies
              that all information provided is true, complete, and accurate to the
              best of their knowledge; (ii) authorizes MerchantCore and its
              acquiring partners to verify any of the information provided,
              including credit and background checks on the business and its
              principal owners; and (iii) agrees to be contacted by MerchantCore
              regarding this application.
            </p>
          </div>

          <div className="mt-8 grid gap-5 sm:grid-cols-3">
            <Field label="Authorized signer (full name)" required>
              <input name="signature_name" required value={form.signature_name} onChange={onChange} className="input" placeholder="Type full legal name" data-testid="apply-signature-name" />
            </Field>
            <Field label="Title" required>
              <input name="signature_title" required value={form.signature_title} onChange={onChange} className="input" placeholder="e.g. CEO" data-testid="apply-signature-title" />
            </Field>
            <Field label="Date" required>
              <input type="date" name="signature_date" required value={form.signature_date} onChange={onChange} className="input" data-testid="apply-signature-date" />
            </Field>
          </div>

          <div className="mt-8">
            <div className="label">Signature preview</div>
            <div className="flex items-end rounded-2xl border border-line bg-white/[0.02] px-6 pb-3 pt-10">
              <span className="text-3xl italic text-emerald sm:text-4xl" style={{ fontFamily: "'Caveat', cursive" }} data-testid="apply-signature-preview">
                {form.signature_name || "— your signature appears here —"}
              </span>
            </div>
            <p className="mt-2 font-mono text-[10px] uppercase tracking-ultra text-faint">
              {form.signature_date} · electronically signed via merchantcore.us
            </p>
          </div>

          <label className="mt-8 flex items-start gap-4 rounded-2xl border border-line bg-white/[0.02] p-5" data-testid="apply-acknowledge-wrapper">
            <input type="checkbox" name="acknowledged" required checked={form.acknowledged} onChange={onChange} className="mt-1 h-5 w-5 accent-[#4ADE9E]" data-testid="apply-acknowledge" />
            <span className="font-sans text-sm leading-relaxed text-cloud/85">
              I acknowledge that all information above is accurate, I am authorized
              to submit this application on behalf of the business, and I consent
              to MerchantCore contacting me about this application.
            </span>
          </label>

          <div className="mt-8 flex flex-wrap items-center justify-between gap-4 border-t border-line pt-6">
            <p className="max-w-md font-mono text-[11px] uppercase tracking-wide2 text-faint">
              Application submitted electronically. MerchantCore operations team
              (ops@merchantcore.us) will follow up within 1 business day.
            </p>
            <button type="submit" disabled={status.state === "loading"} className="btn-primary" data-testid="apply-submit">
              {status.state === "loading" ? "Submitting…" : "Submit application"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </div>

          {status.state === "success" && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-emerald/40 bg-emerald/10 p-4" data-testid="apply-success">
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald" />
              <span className="font-sans text-sm text-cloud">{status.msg}</span>
            </div>
          )}
          {status.state === "error" && (
            <div className="mt-6 flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 p-4" data-testid="apply-error">
              <AlertTriangle className="mt-0.5 h-5 w-5 text-red-400" />
              <span className="font-sans text-sm text-red-300">{status.msg}</span>
            </div>
          )}
        </FormSection>
      </form>
    </>
  );
}

function FormSection({ id, n, title, subtitle, children, last = false }) {
  return (
    <section id={id} className={`scroll-mt-24 ${last ? "pb-24" : ""}`} data-testid={`apply-section-${id}`}>
      <div className="container-wide grid gap-8 py-14 lg:grid-cols-12">
        <div className="lg:col-span-4 lg:sticky lg:top-28 lg:self-start">
          <div className="flex items-center gap-3">
            <span className="font-mono text-[11px] font-medium uppercase tracking-ultra text-emerald">Section {n}</span>
            <span className="h-px flex-1 bg-line" />
          </div>
          <h2 className="mt-4 font-display text-2xl font-semibold tracking-tight text-cloud sm:text-3xl">{title}</h2>
          {subtitle && <p className="mt-3 font-sans text-sm leading-relaxed text-muted">{subtitle}</p>}
        </div>
        <div className="lg:col-span-8">
          <div className="card">{children}</div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="label">
        {label}
        {required && <span className="text-emerald"> *</span>}
      </span>
      {children}
    </label>
  );
}

function OwnerBlock({ idx, form, onChange, required = false }) {
  const k = (suffix) => `owner${idx}_${suffix}`;
  const v = (suffix) => form[k(suffix)];
  return (
    <div data-testid={`apply-owner-${idx}`}>
      <div className="mb-6 flex items-baseline gap-3">
        <span className="font-mono text-[11px] uppercase tracking-ultra text-muted">Owner #{idx}</span>
        <span className="h-px flex-1 bg-line" />
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <Field label="Full name" required={required}><input name={k("full_name")} required={required} value={v("full_name")} onChange={onChange} className="input" data-testid={`apply-owner${idx}-name`} /></Field>
        <Field label="Title" required={required}><input name={k("title")} required={required} value={v("title")} onChange={onChange} className="input" placeholder="e.g. CEO" data-testid={`apply-owner${idx}-title`} /></Field>
        <Field label="Ownership %" required={required}><input name={k("ownership_pct")} required={required} value={v("ownership_pct")} onChange={onChange} className="input" placeholder="e.g. 60" data-testid={`apply-owner${idx}-pct`} /></Field>
        <Field label="Date of birth" required={required}><input type="date" name={k("dob")} required={required} value={v("dob")} onChange={onChange} className="input" data-testid={`apply-owner${idx}-dob`} /></Field>
        <Field label="Social security number" required={required}><input name={k("ssn")} required={required} value={v("ssn")} onChange={onChange} className="input" placeholder="xxx-xx-xxxx" data-testid={`apply-owner${idx}-ssn`} /></Field>
        <Field label="Driver's license #" required={required}><input name={k("drivers_license")} required={required} value={v("drivers_license")} onChange={onChange} className="input" data-testid={`apply-owner${idx}-dl`} /></Field>
        <div className="sm:col-span-2">
          <Field label="Physical address" required={required}><input name={k("address")} required={required} value={v("address")} onChange={onChange} className="input" data-testid={`apply-owner${idx}-address`} /></Field>
        </div>
        <Field label="City" required={required}><input name={k("city")} required={required} value={v("city")} onChange={onChange} className="input" data-testid={`apply-owner${idx}-city`} /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="State" required={required}><input name={k("state")} required={required} value={v("state")} onChange={onChange} className="input" data-testid={`apply-owner${idx}-state`} /></Field>
          <Field label="ZIP" required={required}><input name={k("zip")} required={required} value={v("zip")} onChange={onChange} className="input" data-testid={`apply-owner${idx}-zip`} /></Field>
        </div>
        <Field label="Cell phone" required={required}><input name={k("cell_phone")} required={required} value={v("cell_phone")} onChange={onChange} className="input" data-testid={`apply-owner${idx}-phone`} /></Field>
      </div>
    </div>
  );
}
